import { useMemo } from "react";
import { getColorFromCssVariable } from "ui";
import { LineChartCard } from "./shared/LineChartCard";
import { MetricDefinitions } from "./shared/MetricDefinitions";
import { useDatadogQuery } from "../DatadogAnalytics/useDatadogQuery";

type RevenueData = {
  label: string;
  authorized: number;
  captured: number;
  refunded: number;
}[];

export function TotalRevenueChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<RevenueData>({
      chartType: "TOTAL_REVENUE",
    });

  const { labels, authorized, captured, refunded } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0)
      return { labels: [], authorized: [], captured: [], refunded: [] };

    return {
      labels: data.map((item) => item.label),
      authorized: data.map((item) => item.authorized),
      captured: data.map((item) => item.captured),
      refunded: data.map((item) => item.refunded),
    };
  }, [data]);

  const { authorizedColor, capturedColor, refundedColor } = useMemo(
    () => ({
      authorizedColor: getColorFromCssVariable("--chart-categorical-1-subtle"),
      capturedColor: getColorFromCssVariable("--chart-categorical-2-subtle"),
      refundedColor: getColorFromCssVariable("--chart-categorical-3-subtle"),
    }),
    []
  );

  return (
    <LineChartCard
      ref={ref}
      title="Total revenue"
      description={
        <MetricDefinitions
          definitions={[
            {
              term: "Authorized revenue",
              description:
                "Total value of authorized transactions, including those not yet captured.",
            },
            {
              term: "Captured revenue",
              description: "Total value of successfully captured payments.",
            },
            {
              term: "Refunded revenue",
              description: "Total value refunded to customers.",
            },
          ]}
        />
      }
      format="currency"
      labels={labels}
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
      datasets={[
        {
          label: "Authorized",
          data: authorized,
          backgroundColor: authorizedColor,
          borderColor: authorizedColor,
        },
        {
          label: "Captured",
          data: captured,
          backgroundColor: capturedColor,
          borderColor: capturedColor,
        },
        {
          label: "Refunded",
          data: refunded,
          backgroundColor: refundedColor,
          borderColor: refundedColor,
        },
      ]}
    />
  );
}
